import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  CalendarIcon,
  ClockIcon,
  TagIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
}

const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'announcements', name: 'Announcements' },
    { id: 'tutorials', name: 'Tutorials' },
    { id: 'technology', name: 'Technology' },
    { id: 'ecosystem', name: 'Ecosystem' },
    { id: 'community', name: 'Community' },
  ];

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      slug: 'templum-mainnet-launch',
      title: 'TEMPLUM Launches on Polygon Mainnet',
      excerpt: 'We are thrilled to announce the official mainnet launch of TEMPLUM, bringing decentralized auctions to the Polygon network with four unique auction mechanisms.',
      category: 'announcements',
      author: 'TEMPLUM Team',
      date: 'January 15, 2026',
      readTime: '5 min',
      image: '/blog/launch.jpg',
      featured: true
    },
    {
      id: '2',
      slug: 'understanding-auction-types',
      title: 'Understanding the Four Auction Types on TEMPLUM',
      excerpt: 'A deep dive into English, Dutch, Vickrey, and Candle auctions - learn which type is best suited for different scenarios and assets.',
      category: 'tutorials',
      author: 'Alex Chen',
      date: 'January 12, 2026',
      readTime: '8 min',
      image: '/blog/auction-types.jpg'
    },
    {
      id: '3',
      slug: 'staking-tier-benefits',
      title: 'Maximizing Your Benefits with NDH Staking Tiers',
      excerpt: 'Discover how staking NDH tokens can unlock significant fee discounts, governance rights, and exclusive auction access on TEMPLUM.',
      category: 'tutorials',
      author: 'Maria Garcia',
      date: 'January 10, 2026',
      readTime: '6 min',
      image: '/blog/staking.jpg'
    },
    {
      id: '4',
      slug: 'smart-contract-security',
      title: 'Behind the Code: How We Secure TEMPLUM Smart Contracts',
      excerpt: 'An inside look at our security practices, audit processes, and the technical measures that keep your funds safe on TEMPLUM.',
      category: 'technology',
      author: 'Dev Team',
      date: 'January 8, 2026',
      readTime: '10 min',
      image: '/blog/security.jpg'
    },
    {
      id: '5',
      slug: 'nardiha-ecosystem-overview',
      title: 'TEMPLUM in the NARDIHA Ecosystem: Cross-Platform Synergies',
      excerpt: 'Learn how TEMPLUM integrates with other platforms in the NARDIHA ecosystem and how NDH token holders benefit from the interconnected network.',
      category: 'ecosystem',
      author: 'NARDIHA Team',
      date: 'January 5, 2026',
      readTime: '7 min',
      image: '/blog/ecosystem.jpg'
    },
    {
      id: '6',
      slug: 'community-governance-launch',
      title: 'DAO Governance Goes Live: Your Voice Matters',
      excerpt: 'The TEMPLUM DAO is now active! Learn how to participate in governance, submit proposals, and vote on the future of the platform.',
      category: 'community',
      author: 'Governance Team',
      date: 'January 3, 2026',
      readTime: '5 min',
      image: '/blog/governance.jpg'
    },
    {
      id: '7',
      slug: 'vickrey-auction-strategy',
      title: 'Winning Strategies for Vickrey Auctions',
      excerpt: 'The sealed-bid second-price auction explained: why truthful bidding is your best strategy and how to approach these unique auctions.',
      category: 'tutorials',
      author: 'Alex Chen',
      date: 'December 28, 2025',
      readTime: '6 min',
      image: '/blog/vickrey.jpg'
    },
    {
      id: '8',
      slug: 'certik-audit-complete',
      title: 'CertiK Audit Complete: TEMPLUM Passes with Flying Colors',
      excerpt: 'We are pleased to share that our smart contracts have successfully completed a comprehensive security audit by CertiK.',
      category: 'announcements',
      author: 'Security Team',
      date: 'December 20, 2025',
      readTime: '4 min',
      image: '/blog/certik.jpg'
    }
  ];

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  const filteredPosts = regularPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <BookOpenIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              TEMPLUM <span className="text-primary-400">Blog</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              News, tutorials, and insights from the TEMPLUM team and community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-12 bg-dark-900/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <span className="text-primary-400 text-sm font-semibold mb-4 block">Featured Post</span>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl overflow-hidden border border-primary-500/30"
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="h-64 md:h-auto bg-dark-800 flex items-center justify-center">
                    <span className="text-6xl font-bold text-primary-500/30">T</span>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                      <span className="px-3 py-1 bg-primary-500/20 text-primary-400 rounded-full capitalize">
                        {featuredPost.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {featuredPost.date}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-300 mb-6">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm text-gray-400">
                        <UserCircleIcon className="w-5 h-5" />
                        {featuredPost.author}
                        <span>•</span>
                        <ClockIcon className="w-4 h-4" />
                        {featuredPost.readTime}
                      </div>
                      <Link
                        to={`/blog/${featuredPost.slug}`}
                        className="flex items-center gap-2 text-primary-400 hover:text-primary-300"
                      >
                        Read More
                        <ArrowRightIcon className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Search and Filter */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
              {/* Search */}
              <div className="relative w-full md:w-80">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-dark-800 text-gray-400 hover:bg-dark-700'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post, idx) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl overflow-hidden border border-dark-700 hover:border-primary-500/50 transition-colors group"
                  >
                    <div className="h-48 bg-dark-700 flex items-center justify-center">
                      <span className="text-4xl font-bold text-dark-600 group-hover:text-primary-500/30 transition-colors">
                        T
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                        <span className="px-2 py-0.5 bg-dark-700 rounded capitalize">{post.category}</span>
                        <span>{post.date}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {post.readTime}
                        </span>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-primary-400 hover:text-primary-300 flex items-center gap-1"
                        >
                          Read
                          <ArrowRightIcon className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400">No articles found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchQuery('');
                  }}
                  className="mt-4 text-primary-400 hover:text-primary-300"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-gray-300 mb-6">
              Subscribe to our newsletter for the latest news, tutorials, and platform updates.
            </p>
            <form className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-dark-800 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
            <p className="text-gray-500 text-sm mt-3">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
